/*
  assets/collection-grid-with-promos.js
  Handles: filter drawer toggle, color swatch image/price swap,
  AJAX add-to-cart for the Quick Add / Select Options button, and a
  guest-only wishlist stored in localStorage (no app dependency —
  persists per-browser only, does not sync to a customer account).
  Loaded as a real asset (not an inline <script>) so it isn't affected
  by themes running a strict Content-Security-Policy that blocks
  inline scripts.

  Shared by both sections/collection-grid-with-promos.liquid and
  sections/collection-carousel.liquid (same card markup, same
  wishlist/swatch/quick-add behavior). If both sections ever end up on
  the same page, this file would load twice — the guard below makes
  that safe instead of double-firing every click handler.
*/
(function () {
  if (window.__cgpInitialized) return;
  window.__cgpInitialized = true;

  var WISHLIST_KEY = 'cgp_wishlist_v1';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function getWishlist() {
    try {
      var raw = localStorage.getItem(WISHLIST_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.warn('[collection-grid-with-promos] Could not read wishlist from localStorage:', e);
      return {};
    }
  }

  function setWishlist(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (e) {
      console.warn('[collection-grid-with-promos] Could not save wishlist to localStorage:', e);
    }
    document.dispatchEvent(new CustomEvent('cgp:wishlist-updated', {
      detail: { count: Object.keys(list).length, items: list }
    }));
  }

  // Marks every wishlist heart on the page as filled/unfilled based on
  // what's actually saved — not a guess, the real current state.
  function initWishlistState() {
    var list = getWishlist();
    document.querySelectorAll('[data-cgp-wish]').forEach(function (btn) {
      var id = btn.dataset.productId;
      if (id && list[id]) btn.classList.add('is-active');
    });
  }

  ready(function () {
    initWishlistState();

    // --- Filter drawer toggle ---
    document.querySelectorAll('[data-cgp-filter-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = document.querySelector('[data-cgp-filters]');
        if (!panel) return;
        var open = !panel.hasAttribute('hidden');
        if (open) {
          panel.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
        } else {
          panel.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // --- Color swatch click: swap image/price, wire up the variant to add ---
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest('.cgp-swatch');
      if (swatch) {
        handleSwatchClick(swatch);
        return;
      }

      var wishBtn = e.target.closest('[data-cgp-wish]');
      if (wishBtn) {
        handleWishlistClick(wishBtn);
        return;
      }

      var addBtn = e.target.closest('[data-cgp-quickadd-btn]');
      if (addBtn) {
        handleAddToCart(e, addBtn);
      }
    });
  });

  function handleWishlistClick(wishBtn) {
    var id = wishBtn.dataset.productId;
    if (!id) return;

    var list = getWishlist();

    if (list[id]) {
      delete list[id];
      wishBtn.classList.remove('is-active');
      wishBtn.setAttribute('aria-label', 'Add ' + (wishBtn.dataset.productTitle || 'product') + ' to wishlist');
    } else {
      list[id] = {
        id: id,
        handle: wishBtn.dataset.productHandle || '',
        title: wishBtn.dataset.productTitle || '',
        url: wishBtn.dataset.productUrl || '',
        image: wishBtn.dataset.productImage || '',
        price: wishBtn.dataset.productPrice || '',
        addedAt: Date.now()
      };
      wishBtn.classList.add('is-active');
      wishBtn.setAttribute('aria-label', 'Remove ' + (wishBtn.dataset.productTitle || 'product') + ' from wishlist');
    }

    setWishlist(list);
  }

  function handleSwatchClick(swatch) {
    var swatchGroup = swatch.closest('[data-cgp-swatches]');
    var card = swatch.closest('[data-cgp-product]');
    if (!swatchGroup || !card) return;

    swatchGroup.querySelectorAll('.cgp-swatch').forEach(function (s) {
      s.classList.remove('is-selected');
    });
    swatch.classList.add('is-selected');

    var img = card.querySelector('[data-cgp-primary-img]');
    if (img && swatch.dataset.image) {
      img.src = swatch.dataset.image;
      if (swatch.dataset.srcset) img.srcset = swatch.dataset.srcset;
    }

    var nowPrice = card.querySelector('[data-cgp-now]');
    var wasPrice = card.querySelector('[data-cgp-was]');
    if (nowPrice && swatch.dataset.price) nowPrice.textContent = swatch.dataset.price;
    if (wasPrice) {
      if (swatch.dataset.comparePrice) {
        wasPrice.textContent = swatch.dataset.comparePrice;
        wasPrice.style.display = '';
      } else {
        wasPrice.style.display = 'none';
      }
    }

    // Always wire the hidden variant input to whatever color is selected,
    // regardless of how many total options the product has. This is the
    // best available match — if the product also has e.g. a Size option,
    // the added variant will be that color's first available combination.
    var variantInput = card.querySelector('[data-cgp-variant-input]');
    var addBtn = card.querySelector('[data-cgp-quickadd-btn]');
    if (variantInput && swatch.dataset.variantId) {
      variantInput.value = swatch.dataset.variantId;
    }
    if (addBtn) {
      var available = swatch.dataset.available === 'true';
      addBtn.disabled = !available;
      var label = addBtn.querySelector('[data-cgp-quickadd-label]');
      if (label) label.textContent = available ? label.dataset.defaultLabel || label.textContent : 'Sold Out';
    }
  }

  function handleAddToCart(e, addBtn) {
    var form = addBtn.closest('form');
    if (!form) return; // let it submit normally as a fallback

    var input = form.querySelector('[data-cgp-variant-input]');
    var variantId = input ? input.value : null;
    if (!variantId) return; // fallback to normal form submission

    e.preventDefault();

    var label = addBtn.querySelector('[data-cgp-quickadd-label]');
    var originalText = label ? label.textContent : addBtn.textContent;
    if (label && !label.dataset.defaultLabel) label.dataset.defaultLabel = originalText;

    addBtn.disabled = true;
    if (label) label.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (data) { throw new Error(data.description || 'Could not add to cart'); });
        return res.json();
      })
      .then(function (addedLine) {
        if (label) label.textContent = 'Added';

        // Dispatch the theme's real cart:update event, matching the exact
        // shape assets/cart-icon.js and assets/cart-drawer.js expect
        // (see assets/events.js — CartAddEvent.eventName is 'cart:update',
        // same string as ThemeEvents.cartUpdate, so one event satisfies
        // both listeners). source: 'product-form-component' tells
        // cart-icon.js to ADD itemCount to the current bubble count
        // (a delta), rather than treat it as the new absolute total —
        // which is exactly right since our fetch response is just the
        // added line, not the full cart, so we don't have an absolute
        // total to report without a second request.
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: addedLine,
            sourceId: 'collection-grid-with-promos',
            data: {
              itemCount: addedLine.quantity || 1,
              source: 'product-form-component',
              variantId: variantId
            }
          }
        }));

        setTimeout(function () {
          addBtn.disabled = false;
          if (label) label.textContent = originalText;
        }, 1500);
      })
      .catch(function (err) {
        if (label) label.textContent = 'Try again';
        addBtn.disabled = false;
        console.error('[collection-grid-with-promos] add to cart failed:', err);
        setTimeout(function () {
          if (label) label.textContent = originalText;
        }, 1500);
      });
  }
})();
